import CreativeGridView from '@/Enums/CampaignManagement/CreativeGridView.enum';
import type CreativeType from '@/Enums/Creatives/CreativeType.enum';
import CreativesTableColumns from '@/Enums/Creatives/CreativesTableColumns.enum';
import { IDV360TicketsCreative } from '@/Interfaces/DV360Tickets/Creative.interface';
import VideoUrlCell from '@/components/Grid/VideoUrlCell';
import { CREATIVE_TYPES_ICONS, CREATIVE_TYPES_TITLES } from '@/constants/creativeLibrary';
import RemoveButton from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Table/components/RemoveButton';
import { Tooltip } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ReactElement } from 'react';
import { FieldErrors } from 'react-hook-form';

interface IProps {
  view?: CreativeGridView;
  creativeIds?: number[];
  formErrors?: FieldErrors;
  handleFieldChange?: (
    id: number,
    field: keyof Pick<IDV360TicketsCreative, 'disassociated'>,
  ) => (value: boolean) => void;
  productLimit?: number;
}

export const getColumns = ({
  view = CreativeGridView.LineItemForm,
  handleFieldChange,
  productLimit,
}: IProps): GridColDef[] => {
  const isSummaryView = view === CreativeGridView.LineItemSummary;

  const renderCellWithTooltip = (
    params: GridRenderCellParams<IDV360TicketsCreative, any>,
    renderContent: (isRowDisabled: boolean) => ReactElement,
  ) => {
    const {
      row: { duration, videoUrl },
    } = params;
    const isProductLimitExceeded = !!(productLimit && duration && duration > productLimit);
    const isVideoUrlMissing = !videoUrl;

    const isRowDisabled = isProductLimitExceeded || isVideoUrlMissing;

    const content = renderContent(isRowDisabled);

    if (isRowDisabled) {
      const tooltipTitle = isProductLimitExceeded
        ? `Creative duration is longer than product limit ${productLimit}s`
        : 'Video is processing';

      return <Tooltip title={tooltipTitle}>{content}</Tooltip>;
    }

    return content;
  };

  const baseColumns = [
    {
      field: CreativesTableColumns.CREATIVE_NAME,
      headerName: 'Creative Name',
      flex: 2,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<IDV360TicketsCreative, string>) =>
        renderCellWithTooltip(params, isRowDisabled => (
          <span
            style={{
              textDecoration: params.row.disassociated ? 'line-through' : 'none',
              opacity: isRowDisabled ? 0.5 : 1,
            }}
          >
            {params.value || '—'}
          </span>
        )),
    },
    {
      field: 'id',
      headerName: 'Creative ID',
      flex: 0.5,
      align: 'center',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<IDV360TicketsCreative, number>) =>
        renderCellWithTooltip(params, isRowDisabled => (
          <span style={{ opacity: isRowDisabled ? 0.5 : 1 }}>{params.value || '—'}</span>
        )),
    },
    {
      field: CreativesTableColumns.TYPE,
      headerName: 'Type',
      align: 'center',
      headerAlign: 'center',
      flex: 1.1,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<IDV360TicketsCreative, CreativeType>) =>
        renderCellWithTooltip(params, isRowDisabled => {
          const Icon = CREATIVE_TYPES_ICONS[params.value!];
          return (
            <div className='flex justify-center items-center' style={{ opacity: isRowDisabled ? 0.5 : 1 }}>
              <Icon className={'w-4 mr-1'} />
              <span>{CREATIVE_TYPES_TITLES[params.value!]}</span>
            </div>
          );
        }),
    },
    {
      field: 'videoUrl',
      sortable: false,
      headerName: 'Video Url',
      align: 'center',
      flex: 2,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<IDV360TicketsCreative, string>) =>
        renderCellWithTooltip(params, isRowDisabled => (
          <div style={{ opacity: isRowDisabled ? 0.5 : 1, width: '100%' }}>
            <VideoUrlCell value={params.value ?? ''} />
          </div>
        )),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      flex: 0.5,
      align: 'center',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<IDV360TicketsCreative, number>) =>
        renderCellWithTooltip(params, isRowDisabled => (
          <span style={{ opacity: isRowDisabled ? 0.5 : 1 }}>{params.value ? `${params.value}s` : '—'}</span>
        )),
    },
  ].filter(Boolean) as GridColDef[];

  if (view === CreativeGridView.LineItemModal || isSummaryView) {
    return baseColumns;
  }

  return [
    ...baseColumns,
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'center',
      flex: 0.75,
      disableColumnMenu: true,
      renderCell: ({ row: { id, disassociated } }: GridRenderCellParams<IDV360TicketsCreative>) =>
        disassociated ? '-' : <RemoveButton onRemove={() => handleFieldChange!(id, 'disassociated')(true)} />,
    },
  ];
};
